// 密度可視化のユーティリティ関数

import * as d3 from 'd3';
import type { DensityBandwidth, DensityVisualization, DensityColorScale } from '../../types/graph-builder.types';
import { getSequentialInterpolator } from '../constants/palette-utils';
import { COLORS } from '../constants/colors';

/**
 * データ点の配列
 */
export interface DataPoint {
  x: number;
  y: number;
  index: number;
}

/**
 * Scottの規則を使用してバンド幅を計算する
 *
 * バンド幅は密度推定の滑らかさを制御するパラメータ。
 * Scottの規則: h = n^(-1/6) * σ
 * ここで、nはデータ点数、σは標準偏差
 *
 * @param points データポイント
 * @param xScale X軸スケール（スケール適用後の値で計算するため）
 * @param yScale Y軸スケール
 * @returns バンド幅（ピクセル単位）
 */
function calculateScottBandwidth(
  points: DataPoint[],
  xScale: d3.ScaleLinear<number, number>,
  yScale: d3.ScaleLinear<number, number>
): number {
  if (points.length === 0) return 20; // デフォルト値

  // スケール適用後の座標を取得
  const scaledXValues = points.map(p => xScale(p.x));
  const scaledYValues = points.map(p => yScale(p.y));

  // 標準偏差を計算
  const xStdDev = d3.deviation(scaledXValues) || 1;
  const yStdDev = d3.deviation(scaledYValues) || 1;

  // 平均標準偏差
  const avgStdDev = (xStdDev + yStdDev) / 2;

  // Scottの規則: h = n^(-1/6) * σ
  const n = points.length;
  const bandwidth = Math.pow(n, -1/6) * avgStdDev;

  return bandwidth;
}

/**
 * バンド幅を計算する
 *
 * @param bandwidthSetting バンド幅設定（'auto' または数値）
 * @param points データポイント
 * @param xScale X軸スケール
 * @param yScale Y軸スケール
 * @returns バンド幅（ピクセル単位）
 */
export function calculateBandwidth(
  bandwidthSetting: DensityBandwidth,
  points: DataPoint[],
  xScale: d3.ScaleLinear<number, number>,
  yScale: d3.ScaleLinear<number, number>
): number {
  if (typeof bandwidthSetting === 'number') {
    // 手動設定の場合、データスケールに対する相対値をピクセル値に変換
    // bandwidthSettingは0.01-2.0の範囲の相対値
    const xRange = xScale.domain()[1] - xScale.domain()[0];
    const yRange = yScale.domain()[1] - yScale.domain()[0];
    const avgRange = (xRange + yRange) / 2;

    // 相対値をピクセル値に変換
    const scaledBandwidth = bandwidthSetting * avgRange;
    return Math.max(scaledBandwidth, 1); // 最小値1ピクセル
  }

  // 'auto'の場合、Scottの規則を使用
  return calculateScottBandwidth(points, xScale, yScale);
}

/**
 * 特定の点の密度を推定する（シンプルなKDE近似）
 *
 * バンド幅内にある他の点の数をカウントし、全体の点数で割る。
 *
 * @param point 対象の点
 * @param allPoints 全データポイント
 * @param bandwidth バンド幅（ピクセル単位）
 * @param xScale X軸スケール
 * @param yScale Y軸スケール
 * @returns 密度推定値（0〜1）
 */
export function estimatePointDensity(
  point: DataPoint,
  allPoints: DataPoint[],
  bandwidth: number,
  xScale: d3.ScaleLinear<number, number>,
  yScale: d3.ScaleLinear<number, number>
): number {
  if (allPoints.length === 0) return 0;

  // 対象の点のスケール適用後の座標
  const px = xScale(point.x);
  const py = yScale(point.y);

  // バンド幅内にある点の数をカウント
  let count = 0;
  for (const otherPoint of allPoints) {
    const ox = xScale(otherPoint.x);
    const oy = yScale(otherPoint.y);

    // ユークリッド距離を計算
    const distance = Math.sqrt(Math.pow(px - ox, 2) + Math.pow(py - oy, 2));

    if (distance <= bandwidth) {
      count++;
    }
  }

  // 全体の点数で割って正規化
  return count / allPoints.length;
}

/**
 * 全データポイントの密度を計算し、閾値（10パーセンタイル）を返す
 *
 * @param points データポイント
 * @param bandwidth バンド幅
 * @param xScale X軸スケール
 * @param yScale Y軸スケール
 * @returns 密度の閾値（10パーセンタイル値）
 */
export function calculateDensityThreshold(
  points: DataPoint[],
  bandwidth: number,
  xScale: d3.ScaleLinear<number, number>,
  yScale: d3.ScaleLinear<number, number>
): number {
  if (points.length === 0) return 0;

  // 全点の密度を計算
  const densities = points.map(point =>
    estimatePointDensity(point, points, bandwidth, xScale, yScale)
  );

  // 昇順にソート
  densities.sort((a, b) => a - b);

  // 10パーセンタイル位置を計算
  const percentileIndex = Math.floor(densities.length * 0.1);

  return densities[percentileIndex] || 0;
}

/**
 * DensityColorScaleからパレットIDへのマッピング
 */
const DENSITY_COLOR_SCALE_TO_PALETTE_ID: Record<DensityColorScale, string> = {
  'YlOrRd': 'ylorrd',
  'Blues': 'blues',
  'Greens': 'greens',
  'Greys': 'greys',
  'Purples': 'purples',
  'Viridis': 'viridis',
  'Plasma': 'plasma',
};

/**
 * 密度カラースケールからD3のinterpolatorを取得する
 *
 * 統一パレットシステムを使用して補間関数を取得。
 *
 * @param colorScale カラースケール名
 * @returns D3のcolor interpolator関数
 */
export function getDensityColorInterpolator(colorScale: DensityColorScale): (t: number) => string {
  const paletteId = DENSITY_COLOR_SCALE_TO_PALETTE_ID[colorScale];
  return getSequentialInterpolator(paletteId);
}

/**
 * 可視化スタイルをD3セレクションに適用する
 *
 * @param selection D3セレクション（path要素）
 * @param style 可視化スタイル
 * @param colorScale カラースケール
 */
export function applyVisualizationStyle(
  selection: d3.Selection<SVGPathElement, d3.ContourMultiPolygon, SVGGElement, unknown>,
  style: DensityVisualization,
  colorScale: d3.ScaleSequential<string>
): void {
  switch (style) {
    case 'contour-only':
      // 等高線のみ：塗りつぶしなし、線のみ
      selection
        .style('fill', 'none')
        .style('stroke', d => colorScale(d.value))
        .style('stroke-width', 1.5)
        .style('opacity', 1);
      break;

    case 'filled-only':
      // 塗りつぶしのみ：境界線なし
      selection
        .style('fill', d => colorScale(d.value))
        .style('stroke', 'none')
        .style('opacity', 0.7);
      break;

    case 'filled-with-lines':
      // 塗りつぶしと線の組み合わせ（デフォルト）
      selection
        .style('fill', d => colorScale(d.value))
        .style('stroke', COLORS.visualization.contourStroke)
        .style('stroke-width', 0.5)
        .style('opacity', 0.7);
      break;
  }
}
